import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useRemediation } from '../../context/RemediationContext';
import { trainingModules } from '../../data/trainingModules';
import CompleteRemediationModal from './CompleteRemediationModal';

const { FiSearch, FiAlertTriangle, FiCheckCircle, FiClock, FiXCircle, FiBook, FiTrendingUp, FiTrendingDown } = FiIcons;

const statusConfig = {
  Pending: { color: 'bg-slate-100 text-slate-600 border-slate-200', icon: FiClock },
  'In Progress': { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: FiClock },
  Completed: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: FiCheckCircle },
  Overdue: { color: 'bg-red-100 text-red-700 border-red-200', icon: FiAlertTriangle },
};

const eventSeverity = {
  'Clicked Link': 'bg-orange-100 text-orange-700',
  'Submitted Credentials': 'bg-red-100 text-red-700',
  'Opened Email': 'bg-yellow-100 text-yellow-700',
};

const RemediationTable = () => {
  const { records, learners, completeRemediation, updateRecordStatus } = useRemediation();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [completing, setCompleting] = useState(null);

  const statuses = ['All', 'Pending', 'In Progress', 'Completed', 'Overdue'];

  const enriched = records.map(r => ({
    ...r,
    learner: learners.find(l => l.id === r.learnerId),
    module: trainingModules.find(m => m.id === r.assignedModuleId),
  }));

  const filtered = enriched.filter(r => {
    const name = r.learner ? `${r.learner.firstName} ${r.learner.lastName}`.toLowerCase() : '';
    const matchSearch = !search || name.includes(search.toLowerCase()) || r.triggerEvent.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All' || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search learner or event…"
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {statuses.map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === s ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <th className="p-4">Learner</th>
                <th className="p-4">Trigger Event</th>
                <th className="p-4">Assigned Module</th>
                <th className="p-4">Risk Impact</th>
                <th className="p-4">Due Date</th>
                <th className="p-4">Score</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {filtered.map(r => {
                const cfg = statusConfig[r.status] || statusConfig['Pending'];
                return (
                  <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4">
                      {r.learner ? (
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-violet-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {r.learner.firstName[0]}{r.learner.lastName[0]}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{r.learner.firstName} {r.learner.lastName}</p>
                            <p className="text-xs text-slate-400">{r.learner.department}</p>
                          </div>
                        </div>
                      ) : <span className="text-slate-400 text-xs">{r.learnerId}</span>}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${eventSeverity[r.triggerEvent] || 'bg-slate-100 text-slate-600'}`}>
                        {r.triggerEvent}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1.5 text-xs text-slate-600">
                        <SafeIcon icon={FiBook} className="text-blue-500 flex-shrink-0" />
                        <span className="max-w-36 truncate">{r.module?.title || r.assignedModuleId}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-0.5">
                        <span className="flex items-center gap-1 text-xs font-semibold text-red-600">
                          <SafeIcon icon={FiTrendingUp} className="text-xs" />+{r.riskScoreDelta} pts
                        </span>
                        {r.riskAdjusted && r.riskAdjustmentDelta !== 0 && (
                          <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
                            <SafeIcon icon={FiTrendingDown} className="text-xs" />{r.riskAdjustmentDelta} pts
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-xs text-slate-600">{r.dueDate}</td>
                    <td className="p-4">
                      {r.completionScore !== null ? (
                        <span className={`text-xs font-bold ${r.completionScore >= 80 ? 'text-emerald-600' : r.completionScore >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {r.completionScore}%
                        </span>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </td>
                    <td className="p-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border flex items-center gap-1 w-fit ${cfg.color}`}>
                        <SafeIcon icon={cfg.icon} className="text-xs" />{r.status}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      {(r.status === 'Pending' || r.status === 'In Progress') && (
                        <div className="flex gap-1 justify-end">
                          {r.status === 'Pending' && (
                            <button onClick={() => updateRecordStatus(r.id, 'In Progress')}
                              className="text-xs px-2.5 py-1 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors font-medium">
                              Start
                            </button>
                          )}
                          <button onClick={() => setCompleting(r)}
                            className="text-xs px-2.5 py-1 rounded-lg border border-emerald-200 text-emerald-600 hover:bg-emerald-50 transition-colors font-medium">
                            Complete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">
          Showing {filtered.length} of {records.length} remediation records
        </div>
      </div>

      {completing && (
        <CompleteRemediationModal record={completing} onClose={() => setCompleting(null)}
          onComplete={(id, score) => { completeRemediation(id, score); setCompleting(null); }} />
      )}
    </div>
  );
};

export default RemediationTable;